import { useState } from "react";
import { Package } from "lucide-react";
import { EmptyState } from "@/components/common";
import { notify } from "@/lib/notify";
import { useIngredients } from "@/features/inventory/api";
import { useSetVariantIngredients } from "../api";
import { VariantIngredientEditor } from "./VariantIngredientEditor";
import type { Product } from "@/types";

interface ProductIngredientsEditorProps {
  product: Product;
}

export function ProductIngredientsEditor({
  product,
}: ProductIngredientsEditorProps) {
  const setVariantIngredients = useSetVariantIngredients();
  const { data: ingredientsData } = useIngredients({ limit: 100 });
  const ingredientsList = ingredientsData?.data ?? [];

  const [edited, setEdited] = useState<
    Record<string, { ingredientId: string; quantityUsed: number }[]>
  >({});

  const handleSave = async (variantId: string) => {
    const ingredients = edited[variantId] || [];
    const invalid = ingredients.find(
      (i) => !i.quantityUsed || i.quantityUsed < 1,
    );

    if (invalid) {
      notify.error("Each ingredient quantity must be at least 1");

      return;
    }

    try {
      await setVariantIngredients.mutateAsync({ variantId, ingredients });
      notify.success("Ingredients saved");
    } catch (err) {
      notify.fromError(err, "Failed to save ingredients");
    }
  };

  if (!product.variants?.length) {
    return (
      <EmptyState
        icon={Package}
        title="No variants"
        description="Add variants to this product first"
      />
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure which ingredients each variant uses and in what quantity.
      </p>
      {product.variants.map((variant) => (
        <VariantIngredientEditor
          key={variant.id}
          variantId={variant.id}
          variantName={variant.name}
          variantSku={variant.sku}
          ingredientsList={ingredientsList}
          current={edited[variant.id]}
          onChange={(ingredients) =>
            setEdited((prev) => ({ ...prev, [variant.id]: ingredients }))
          }
          onSave={() => handleSave(variant.id)}
          saving={setVariantIngredients.isPending}
        />
      ))}
    </div>
  );
}
